import { useEffect, useState } from 'react';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import useHasPermission from '../../../../hooks/useHasPermission';
import useIntl from '../../../../hooks/useIntl';
import useUpdateDesiredMoveInDateMutation from '../../../../hooks/data-modifying/useUpdateDesiredMoveInDateMutation';
import messages from './messages';

type DateInput = ?(string | Object);

type Params = {
  organizationId: string,
  propertyId: string,
  prospectId: string,
  moveInDateFrom: DateInput,
  onSaved?: () => void,
};

const toMomentOrNull = (date: DateInput) => (date ? moment(date) : null);

// Date-only value: formatted in local time so the picked day never shifts
// across UTC.
const toDay = (date: DateInput) => (date ? formatDateDB(date, false) : null);

export const useDesiredMoveInDateRow = ({
  organizationId,
  propertyId,
  prospectId,
  moveInDateFrom,
  onSaved,
}: Params) => {
  const intl = useIntl();
  const canEdit = useHasPermission('prospect-update');

  // Keyed on the day, not the object: the application selector rebuilds the
  // moment on every refetch.
  const savedDay = toDay(moveInDateFrom);
  const savedDate = toMomentOrNull(savedDay);

  const [displayDate, setDisplayDate] = useState(savedDate);
  const [pickerError, setPickerError] = useState(null);
  // Bumped by revert() so index.js can remount the picker: MUI's internal
  // draft only resyncs to a controlled value when the value itself changes,
  // so reverting to the same saved day (e.g. same-day edit) would otherwise
  // leave the stale, invalid text on screen after blur.
  const [pickerResetKey, setPickerResetKey] = useState(0);

  useEffect(() => {
    setDisplayDate(toMomentOrNull(savedDay));
  }, [savedDay]);

  const revert = () => {
    setDisplayDate(toMomentOrNull(savedDay));
    setPickerError(null);
    setPickerResetKey((key) => key + 1);
  };

  const mutation = useUpdateDesiredMoveInDateMutation({
    organizationId,
    propertyId,
    prospectId,
    options: {
      onSuccess: () => {
        toastr.success('Success', intl.formatMessage(messages.saved));
        onSaved?.();
      },
      onError: (error) => {
        toastr.error(
          'Error',
          typeof error === 'string'
            ? error
            : intl.formatMessage(messages.saveFailed),
        );
        revert();
      },
    },
  });

  const saveDate = (date: DateInput) => {
    if (mutation.isLoading) return;
    // Nothing saved and nothing entered is not a change. Reverting here would
    // remount the picker on the blur its own calendar causes when it opens.
    if (!date && !savedDay) return;

    const candidate = toMomentOrNull(date);
    if (!candidate?.isValid()) {
      revert();
      return;
    }
    if (savedDate && candidate.isSame(savedDate, 'day')) return;

    mutation.mutate({ moveInDateFrom: toDay(candidate) });
  };

  const datePickerProps = {
    name: 'desiredMoveInDate',
    value: displayDate,
    format: 'MMM DD, YYYY',
    // Same rule as the Prospect tab's Starting Move-In picker. A typed past
    // day surfaces through onError and is reverted on blur.
    disablePast: true,
    disabled: mutation.isLoading,
    // null, not undefined: only null hides the empty helper-text row.
    helperText: null,
    onChange: (date: DateInput) => {
      setDisplayDate(date);
    },
    onAccept: saveDate,
    onError: setPickerError,
    onBlur: () => {
      // A saved past day fails disablePast before anything is typed, and clicking
      // inside the calendar blurs the input; reverting then remounts the picker
      // and closes the calendar mid-navigation.
      const isSavedDayShown = savedDate
        ? Boolean(displayDate?.isSame(savedDate, 'day'))
        : !displayDate;
      if (pickerError && !isSavedDayShown) {
        revert();
        return;
      }
      saveDate(displayDate);
    },
  };

  return { canEdit, savedDate, pickerResetKey, datePickerProps };
};
